import React from 'react';
import { Home, Compass, MessageSquare, User, Flame } from 'lucide-react';
import { useRandomCounter } from '../hooks/useRandomCounter';

interface NavItem {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function NavItem({ icon, label, isActive, onClick }: NavItem) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center py-2 ${
        isActive ? 'text-blue-500' : 'text-gray-400'
      }`}
    >
      <div className={`relative ${isActive ? 'animate-bounce-subtle' : ''}`}>
        {icon}
        {isActive && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
        )}
      </div>
      <span className="text-[10px] mt-1">{label}</span>
    </button>
  );
}

interface BottomNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export function BottomNav({ activeTab, onTabChange }: BottomNavProps) {
  const showCounter = useRandomCounter();

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-lg border-t border-gray-800 safe-area-bottom z-[60]">
      <div className="flex items-center justify-around px-2 h-14">
        <NavItem
          icon={<Home className="w-6 h-6" />}
          label="Home"
          isActive={activeTab === 'home'}
          onClick={() => onTabChange('home')}
        />
        <NavItem
          icon={<Compass className="w-6 h-6" />}
          label="Discover"
          isActive={activeTab === 'discover'}
          onClick={() => onTabChange('discover')}
        />
        <div className="relative flex-1 flex flex-col items-center justify-center">
          <button
            onClick={() => onTabChange('trending')}
            className="relative group fire-button -translate-y-3"
          >
            <div className={`relative flex flex-col items-center ${
              activeTab === 'trending' ? 'text-white' : 'text-gray-400'
            }`}>
              <div className={`p-2 rounded-full transition-colors duration-200 ${
                activeTab === 'trending'
                  ? 'bg-gradient-to-br from-blue-600 to-blue-400'
                  : 'bg-gray-800/30 group-hover:bg-gray-800/50'
              }`}>
                <Flame 
                  className={`w-7 h-7 transition-all duration-200 ${
                    activeTab === 'trending' 
                      ? 'text-white scale-110' 
                      : 'text-blue-400 group-hover:text-blue-300'
                  }`}
                />
                {showCounter && (
                  <div className="absolute -top-1 -right-1 bg-blue-400 text-white text-xs font-bold px-1.5 py-0.5 rounded-full animate-[fadeIn_0.3s_ease-out] shadow-lg">
                    +1
                  </div>
                )}
              </div>
              <span className={`text-[10px] mt-1 font-medium tracking-wide transition-colors duration-200 ${
                activeTab === 'trending' ? 'text-blue-400' : 'text-gray-400 group-hover:text-gray-300'
              }`}>
                Trends
              </span>
            </div>
          </button>
        </div>
        <NavItem
          icon={<MessageSquare className="w-6 h-6" />}
          label="Chat"
          isActive={activeTab === 'chat'}
          onClick={() => onTabChange('chat')}
        />
        <NavItem
          icon={<User className="w-6 h-6" />}
          label="Profile"
          isActive={activeTab === 'profile'}
          onClick={() => onTabChange('profile')}
        />
      </div>
    </div>
  );
}